import { Dialog, DialogContent, DialogProps, DialogTitle } from '@mui/material';
import { CategoryScale, Chart as ChartJS, Legend, LinearScale, LineElement, PointElement, Title, Tooltip } from 'chart.js';
import { format, isValid } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import enLocale from 'date-fns/locale/en-US';
import frLocale from 'date-fns/locale/fr-CA';
import useAuth from 'hooks/useAuth';
import useTimezone from 'hooks/useTimezone';
import useUser from 'hooks/useUser';
import { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import { FormattedMessage, useIntl } from 'react-intl';
import DialogClose from '../buttons/DialogClose';
import Legends from './Legends';
import useFormBuilderScores from './useFormBuilderScores';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

interface Props extends DialogProps {
  onClose: () => void;
  assignId: string;
  userId: string;
  formName: string;
}

function getRandomColor() {
  var letters = '0123456789ABCDEF'.split('');
  var color = '#';
  for (var i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
}

const FormBuilderScoreVisualization = ({ onClose, assignId, userId, formName, ...props }: Props) => {
  const intl = useIntl();
  const { userId: currentUserId } = useAuth();
  const { user } = useUser(currentUserId);
  const { timezone } = useTimezone();
  const { scores } = useFormBuilderScores({ userId, assignId });

  const clientSubmissionDates = useMemo(() => {
    const seenDates = new Set();
    return scores?.scores
      ?.map((score) => {
        const submissionMoment = new Date(score?.clientSubmissionDate);
        const formattedDate = isValid(submissionMoment)
          ? format(utcToZonedTime(submissionMoment, timezone), intl.locale === 'fr' ? 'do MMMM yyyy, HH:mm' : 'MMMM do yyyy, h:mm a', {
              locale: intl.locale === 'fr' ? frLocale : enLocale
            })
          : '-';

        // Ensure unique dates
        if (!seenDates.has(formattedDate)) {
          seenDates.add(formattedDate);
          return formattedDate;
        }
        return null; // Skip duplicates
      })
      .filter(Boolean); // Remove null entries
  }, [intl.locale, scores?.scores, timezone]);

  const itemIdToLabelMapping = {};
  const itemIdToClientResponseScoresMapping = {};

  scores?.scores?.forEach((score) => {
    if (!itemIdToLabelMapping[score?.id]) {
      itemIdToLabelMapping[score?.id] = user?.preferredLanguage === 'english' ? score?.label : score?.labelFrench || score?.label;
    }
    if (!itemIdToClientResponseScoresMapping[score?.id]) {
      itemIdToClientResponseScoresMapping[score?.id] = [score?.score];
    } else {
      itemIdToClientResponseScoresMapping[score?.id].push(score?.score);
    }
  });
  const data1 = {
    labels: clientSubmissionDates,
    datasets: Object.entries(itemIdToLabelMapping)?.map(([key, value]) => {
      return {
        label: value,
        data: itemIdToClientResponseScoresMapping[key],
        fill: false,
        lineTension: 0.5,
        backgroundColor: getRandomColor(),
        borderColor: getRandomColor(),
        borderWidth: 2
      };
    }) as any
  };
  const options: any = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom'
      },
      title: {
        display: true,
        text: intl.formatMessage({ defaultMessage: 'Score History' })
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: intl.formatMessage({ defaultMessage: 'Submission Dates' })
        }
      },
      y: {
        title: {
          display: true,
          text: intl.formatMessage({ defaultMessage: 'Scores' })
        }
      }
    }
  };

  return (
    <Dialog maxWidth="sm" fullWidth sx={{ '& .MuiDialog-paper': { p: 0 } }} onClose={onClose} {...props}>
      <DialogTitle>
        <FormattedMessage defaultMessage="Score History" />
        <DialogClose onClose={onClose} />
      </DialogTitle>
      <DialogContent>
        <Line data={data1} options={options} />
        <Legends legends={Object.entries(scores?.legends || {})} />
      </DialogContent>
    </Dialog>
  );
};

export default FormBuilderScoreVisualization;
